/**
 * Turns the announcement bar into a continuous right-to-left ticker.
 *
 * The section renders one group of announcements. This clones that group until it covers the bar
 * twice over, then the CSS animation slides the row by -50%, so the second half is always taking
 * the place of the first and the loop has no visible seam.
 */
class AnnouncementTicker extends HTMLElement {
  #resizeObserver = null;
  #barWidth = 0;
  #groupWidth = 0;

  connectedCallback() {
    this.row = this.querySelector('[data-ticker-row]');
    this.group = this.querySelector('[data-ticker-group]');

    if (!this.row || !this.group) return;

    // The slideshow markup hides every slide but the first; the ticker shows them all.
    for (const slide of this.group.querySelectorAll('[aria-hidden="true"]')) {
      slide.removeAttribute('aria-hidden');
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#fill();

    // Watching the group as well as the bar catches late web fonts, which change how wide the
    // messages are and therefore how many copies the loop needs.
    this.#resizeObserver = new ResizeObserver(() => this.#fill());
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.group);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #fill() {
    const barWidth = this.clientWidth;
    const groupWidth = this.group.getBoundingClientRect().width;

    if (!groupWidth || (barWidth === this.#barWidth && groupWidth === this.#groupWidth)) return;

    this.#barWidth = barWidth;
    this.#groupWidth = groupWidth;

    for (const clone of this.row.querySelectorAll('[data-ticker-clone]')) {
      clone.remove();
    }

    const copies = Math.max(1, Math.ceil(barWidth / groupWidth));

    for (let index = 1; index < copies * 2; index++) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('data-ticker-clone', '');
      clone.setAttribute('aria-hidden', 'true');

      // Duplicate ids and editor handles would confuse both the page and the theme editor.
      for (const node of clone.querySelectorAll('[id]')) node.removeAttribute('id');
      for (const node of clone.querySelectorAll('[data-shopify-editor-block]')) {
        node.removeAttribute('data-shopify-editor-block');
      }

      // Clones stay clickable — a visitor shouldn't care which copy they caught — but they're
      // skipped by the keyboard and by screen readers, which already have the original.
      for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');

      this.row.append(clone);
    }

    const speed = Number(this.dataset.speed) || 45;
    this.row.style.setProperty('--ticker-duration', `${(groupWidth * copies) / speed}s`);
  }
}

if (!customElements.get('announcement-ticker')) {
  customElements.define('announcement-ticker', AnnouncementTicker);
}
